import EnvironmentDevice from "@/models/server/EnvironmentDevice";
import EnvironmentSensor from "@/models/server/EnvironmentSensor";
import {EnvironmentDeviceType} from "@/models/Enums";
import { IconsEnum } from '@renta-apps/renta-react-components';

const UNITS: { [id: string] : number; } = {
    day   : 24 * 60 * 60 * 1000,
    hour  : 60 * 60 * 1000,
    minute: 60 * 1000,
    second: 1000
};

const SENSORS_ORDER: number[] = [1, 0, 5, 3];

type DeviceTypeToSensorMap = {
    [key in EnvironmentDeviceType]: SensorIdToIconMap;
};

type SensorIdToIconMap = {
    [key: number]: IconsEnum;
};
export default class EnvironmentControlHelper {
    static orderSensors(device: EnvironmentDevice, order: number[] = SENSORS_ORDER): EnvironmentSensor[] {
        if (device.type !== EnvironmentDeviceType.Structure) {
            return device.sensors;
        }

        return [...(device.sensors ?? [])].sort((a: EnvironmentSensor, b: EnvironmentSensor) => {
            return order.indexOf(a.id) - order.indexOf(b.id);
        });
    }

    static getSensorRefreshTime(sensor: EnvironmentSensor, language: string, style: Intl.RelativeTimeFormatStyle = "long"): string {
        if (!sensor.timestamp) {
            return '';
        }

        const rtf = new Intl.RelativeTimeFormat(language, { style });
        const date = new Date(sensor.timestamp);
        const now = new Date();
        const diff = date.getTime() - now.getTime();

        for (const u in UNITS) {
            // 1-179 minutes, 3-71 hours, 3... days
            if (u === 'second' || Math.abs(diff) >= (u === 'minute' ? UNITS[u] : UNITS[u] * 3)) {
                return rtf.format(Math.ceil(diff / UNITS[u]), u as Intl.RelativeTimeFormatUnit);
            }
        }

        return '';
    }

    static getSensorUnit(sensor: EnvironmentSensor): string {
        const unitOfMeasurement = sensor.physicalQuantity === 'particulates' ? 'particulates' : (sensor.unitOfMeasurement ?? '');

        return EnvironmentSensor.transFormUnit(unitOfMeasurement);
    };

    public static readonly sensorIconMapping: DeviceTypeToSensorMap =  {
        [EnvironmentDeviceType.Structure]: {
            0: IconsEnum.StructureTemperature,
            1: IconsEnum.StructureTemperature,
            3: IconsEnum.StructureHumidity,
            5: IconsEnum.StructureHumidity,
        },
        [EnvironmentDeviceType.Temperature]: {
            0: IconsEnum.AirTemperature,
            1: IconsEnum.AirTemperature,
            5: IconsEnum.AirHumidity,
        },
        [EnvironmentDeviceType.Pressure]: {
            0: IconsEnum.AirTemperature,
            1: IconsEnum.AirTemperature,
            5: IconsEnum.AirHumidity,
        },
        [EnvironmentDeviceType.Particles]: {
            1: IconsEnum.AirTemperature,
            5: IconsEnum.AirHumidity,
            9: IconsEnum.Dust,
        },
        [EnvironmentDeviceType.Unknown]: {},
        [EnvironmentDeviceType.Gateway]: {},
    };

    static isStructuralSensor = (sensor: EnvironmentSensor, type: EnvironmentDeviceType): boolean => {
        // 1 - structure temperature, 5 - structure humidity
        // Appendix 11 of the IO Living documentation:
        // 0 -- Temperature 2
        // 1 -- Temperature 1
        // 3 -- Relative humidity 2
        // 4 -- CO2
        // 5 -- Relative humidity 1
        // From Teemu Lankila:
        // Typically if one measurement device has both 1 and 2 then 1 is "external" sensor and 2 is "internal" sensor.
        // I need to say typically since we also have devices which have two external sensors, but I don't know if Renta has any of those.
        // If device has only one temperature/humidity then it might be 1 or 2, but again most likely 1 is something that is really outside of the box.
        // In our talk "external" means outside of the plastic box, so it's the "structural" one.
        return [1, 5].includes(sensor.id) && type === EnvironmentDeviceType.Structure;
    };
}